import { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useCurrentProfile } from "@/lib/CurrentProfileContext";
import { supabase } from "@/lib/supabaseClient";
import type { Case } from "@/lib/useRealtimeCases";
import { CaseDrawer } from "@/components/kanban/CaseDrawer";
import { ManagerTasksTab } from "@/components/manager/ManagerTasksTab";
import { ManagerTeamTab } from "@/components/manager/ManagerTeamTab";

type ManagerTab = "tasks" | "team";

const TABS: { id: ManagerTab; label: string }[] = [
  { id: "tasks", label: "Tâches" },
  { id: "team", label: "Équipe" },
];

/** Onglet Manager : suivi des tâches assignées et gestion des équipes par centre (rôle sales_manager). */
export default function ManagerPage() {
  const { isManager, loading } = useCurrentProfile();
  const [params, setParams] = useSearchParams();
  const tab: ManagerTab = params.get("onglet") === "equipe" ? "team" : "tasks";
  const [openCase, setOpenCase] = useState<Case | null>(null);
  const [openingCaseId, setOpeningCaseId] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-sm text-gray-500" role="status">
        Chargement…
      </div>
    );
  }
  if (!isManager) return <Navigate to="/kanban" replace />;

  const selectTab = (next: ManagerTab) => {
    const copy = new URLSearchParams(params);
    if (next === "team") copy.set("onglet", "equipe");
    else copy.delete("onglet");
    setParams(copy, { replace: true });
  };

  const openCaseById = async (caseId: string) => {
    if (openingCaseId) return;
    setOpeningCaseId(caseId);
    setOpenError(null);
    const { data, error } = await supabase.from("cases").select("*").eq("id", caseId).single();
    setOpeningCaseId(null);
    if (error || !data) setOpenError("Impossible d'ouvrir ce dossier (supprimé ou inaccessible).");
    else setOpenCase(data as Case);
  };

  return (
    <div className="min-h-[calc(100vh-57px)] bg-gray-50">
      <div className="mx-auto max-w-[1440px] px-6 pb-10 pt-5">
        <div className="mb-4 flex items-end justify-between border-b border-gray-200">
          <div className="pb-3">
            <h1 className="text-xl font-semibold text-gray-900">Manager</h1>
            <p className="mt-0.5 text-sm text-gray-500">Suivi des tâches assignées et des équipes par centre</p>
          </div>
          <div role="tablist" aria-label="Sections manager" className="flex gap-1">
            {TABS.map((item) => {
              const active = tab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => selectTab(item.id)}
                  className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                    active ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {openError && (
          <div role="alert" className="mb-4 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
            {openError}
            <button type="button" onClick={() => setOpenError(null)} className="font-medium underline">
              Fermer
            </button>
          </div>
        )}
        {openingCaseId && (
          <div role="status" className="fixed bottom-4 left-1/2 z-[70] -translate-x-1/2 rounded-md bg-gray-900 px-3 py-1.5 text-xs text-white shadow">
            Ouverture du dossier…
          </div>
        )}

        <div role="tabpanel">{tab === "tasks" ? <ManagerTasksTab onOpenCase={(id) => void openCaseById(id)} /> : <ManagerTeamTab />}</div>
      </div>
      {openCase && <CaseDrawer case={openCase} onClose={() => setOpenCase(null)} />}
    </div>
  );
}
